const GA_TRACKING_ID = 'UA-81216909-1';
const TRACKING_VERSION = '1';
const NULL_VALUE = '(not set)';

const dimensions: Record<string, string> = {
  TRACKING_VERSION: 'web_version',
  CLIENT_ID: 'client_id',
  WINDOW_ID: 'window_id',
  HIT_ID: 'hit_id',
  HIT_TIME: 'hit_time',
  HTI_TYPE: 'hit_type',
  HIT_SOURCE: 'hit_source',
  VISIBILITY_STATE: 'visibility',
};

const metrics: Record<string, string> = {
  RESPONSE_END_TIME: 'resp_end_time',
  DOM_LOAD_TIME: 'dom_load_time',
  WINDOW_LOAD_TIME: 'window_load_time',
};

declare const ga: any;

export const init = () => {
  // Initialize the command queue in case analytics.js hasn't loaded yet.
  (window as any).ga =
    (window as any).ga || ((...args: any) => (ga.q = ga.q || []).push(args));

  createTracker();
  trackErrors();
  ga('set', dimensions.WINDOW_ID, uuid());
  trackCustomDimensions();
  sendInitialPageview();
  sendNavigationTimingMetrics();
  setTimeout(() => {
    const fonts = document.createElement('link');
    fonts.setAttribute('rel', 'stylesheet');
    fonts.setAttribute('type', 'text/css');
    fonts.setAttribute(
      'href',
      'https://fonts.googleapis.com/css2?family=Montserrat&family=Poppins&display=swap'
    );
    document.getElementsByTagName('head')[0].appendChild(fonts);
  }, 0);
};

/**
 * Tracks a JavaScript error with optional fields object overrides.
 * This function is exported so it can be used in other parts of the codebase.
 * E.g.:
 *
 *    `fetch('/api.json').catch(trackError);`
 *
 * @param {(Error|Object)=} err
 * @param {Object=} fieldsObj
 */
export const trackError = (err = {}, fieldsObj = {}) => {
  ga(
    'send',
    'event',
    Object.assign(
      {
        eventCategory: 'Error',
        eventAction: (err as any).name || '(no error name)',
        eventLabel: `${(err as any).message}\n${
          (err as any).stack || '(no stack trace)'
        }`,
        nonInteraction: true,
      },
      fieldsObj
    )
  );
};

/**
 * Creates the trackers and sets the default transport and tracking
 * version fields. In non-production environments it also logs hits.
 */
const createTracker = () => {
  ga('create', GA_TRACKING_ID, 'auto');
  // Ensures all hits are sent via `navigator.sendBeacon()`.
  ga('set', 'transport', 'beacon');
};

/**
 * Tracks any errors that may have occured on the page prior to analytics being
 * initialized, then adds an event handler to track future errors.
 */
const trackErrors = () => {
  // Errors that have occurred prior to this script running are stored on
  // `window.__e.q`, as specified in `index.html`.
  const loadErrorEvents = ((window as any).__e && (window as any).__e.q) || [];

  const trackErrorEvent = (event: any) => {
    // Use a different eventCategory for uncaught errors.
    const fieldsObj = { eventCategory: 'Uncaught Error' };

    // Some browsers don't have an error property, so we fake it.
    const err = event.error || {
      message: `${event.message} (${event.lineno}:${event.colno})`,
    };

    trackError(err, fieldsObj);
  };

  // Replay any stored load error events.
  for (const event of loadErrorEvents) {
    trackErrorEvent(event);
  }

  // Add a new listener to track event immediately.
  window.addEventListener('error', trackErrorEvent);
};

/**
 * Sets a default dimension value for all custom dimensions on all trackers.
 */
const trackCustomDimensions = () => {
  // Sets a default dimension value for all custom dimensions to ensure
  // that every dimension in every hit has *some* value. This is necessary
  // because Google Analytics will drop rows with empty dimension values
  // in your reports.
  ga('set', dimensions.TRACKING_VERSION, TRACKING_VERSION);
  // Adds tracking of dimensions known at page load time.
  ga((tracker: any) => {
    tracker.set({
      [dimensions.TRACKING_VERSION]: TRACKING_VERSION,
      [dimensions.CLIENT_ID]: tracker.get('clientId'),
      [dimensions.WINDOW_ID]: uuid(),
    });
  });

  // Adds tracking to record each the type, time, uuid, and visibility state
  // of each hit immediately before it's sent.
  ga((tracker: any) => {
    const originalBuildHitTask = tracker.get('buildHitTask');
    tracker.set('buildHitTask', (model: any) => {
      const qt = model.get('queueTime') || 0;
      // tslint:disable-next-line: new-parens
      model.set(
        dimensions.HIT_TIME,
        (new Date().getTime() - qt).toString(),
        true
      );
      model.set(dimensions.HIT_ID, uuid(), true);
      model.set(dimensions.HIT_TYPE, model.get('hitType'), true);
      model.set(dimensions.VISIBILITY_STATE, document.visibilityState, true);

      originalBuildHitTask(model);
    });
  });
};

/**
 * Sends the initial pageview to Google Analytics.
 */
const sendInitialPageview = () => {
  ga('send', 'pageview', { [dimensions.HIT_SOURCE]: 'pageload' });
};

/**
 * Gets the DOM and window load times and sends them as custom metrics to
 * Google Analytics via an event hit.
 */
const sendNavigationTimingMetrics = () => {
  // Only track performance in supporting browsers.
  if (!(window.performance && window.performance.timing)) {
    return;
  }

  // If the window hasn't loaded, run this function after the `load` event.
  if (document.readyState !== 'complete') {
    window.addEventListener('load', sendNavigationTimingMetrics);
    return;
  }

  const nt = performance.timing;
  const navStart = nt.navigationStart;

  const responseEnd = Math.round(nt.responseEnd - navStart);
  const domLoaded = Math.round(nt.domContentLoadedEventStart - navStart);
  const windowLoaded = Math.round(nt.loadEventStart - navStart);

  // In some edge cases browsers return very obviously incorrect NT values,
  // e.g. 0, negative, or future times. This validates values before sending.
  const allValuesAreValid = (...values: any) => {
    return values.every((value: any) => value > 0 && value < 6e6);
  };

  if (allValuesAreValid(responseEnd, domLoaded, windowLoaded)) {
    ga('send', 'event', {
      eventCategory: 'Navigation Timing',
      eventAction: 'track',
      eventLabel: NULL_VALUE,
      nonInteraction: true,
      [metrics.RESPONSE_END_TIME]: responseEnd,
      [metrics.DOM_LOAD_TIME]: domLoaded,
      [metrics.WINDOW_LOAD_TIME]: windowLoaded,
    });
    const currentUrl = window.location.href;
    ga('send', 'event', {
      eventCategory: 'WEB.APPLICATION.LOAD_COMPLETE',
      eventAction: currentUrl,
      eventLabel: NULL_VALUE,
      nonInteraction: true,
    });
    if (currentUrl.includes('quick-profile')) {
      ga('send', 'event', {
        eventCategory: 'WEB.QUICK_PROFILE.START',
        eventAction: currentUrl,
        eventLabel: NULL_VALUE,
        nonInteraction: true,
      });
    }
  }
};

/**
 * Generates a UUID.
 * https://gist.github.com/jed/982883
 * @param {number|undefined=} a
 * @return {string}
 */
const uuid = function b(a?: any): string {
  // tslint:disable-next-line: no-bitwise
  return a
    ? (a ^ ((Math.random() * 16) >> (a / 4))).toString(16)
    : `${1e7}-${1e3}-${4e3}-${8e3}-${1e11}`.replace(/[018]/g, b);
};

export const addScript = (payload: any) => {
  const scriptElem = document.createElement('script');
  scriptElem.setAttribute('src', payload.src);
  scriptElem.setAttribute('type', 'text/javascript');
  document.head.appendChild(scriptElem);
  scriptElem.onload = () => {
    if (payload.windowVar && payload.target) {
      (window as any)[payload.windowVar] = payload.target;
    }
  };
};
